<template>
    <el-dialog title="3D模型预览" :visible.sync="dialogVisible" width="80%" :before-close="handleClose">
        <div class="model-container" ref="modelContainer">
            <div v-if="loading" class="loading-overlay">
                <div class="loading-content">
                    <p class="loading-text">正在加载模型: {{ loadProgress }}%</p>
                    <el-progress :percentage="loadProgress" :stroke-width="6"
                        :stroke-color="loadProgress < 100 ? '#409EFF' : '#67C23A'"></el-progress>
                </div>
            </div>
        </div>

        <div slot="footer" class="dialog-footer">
            <span class="text-sm text-gray-500">提示: 鼠标拖动可旋转模型，滚轮可缩放</span>
        </div>
    </el-dialog>
</template>

<script>
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';

export default {
    name: "threeDialog",
    data() {
        return {
            dialogVisible: false,
            scene: null,
            camera: null,
            renderer: null,
            controls: null,
            mesh: null,
            loading: false,
            loadProgress: 0,
            animationId: null,
            modelFile: "",
        }
    },
    methods: {
        formShow(file) {
            this.modelFile = file || "";
            this.dialogVisible = true
        },

        // 初始化Three.js场景
        initThree() {
            const container = this.$refs.modelContainer
            const width = container.clientWidth
            const height = container.clientHeight

            this.loading = true
            this.loadProgress = 0

            // 创建场景 - 使用更亮的背景色
            this.scene = new THREE.Scene()
            // this.scene.background = new THREE.Color(0xffffff) // 纯白色背景
            this.scene.background = null // 纯白色背景


            // 关键：增强光源设置
            this.setupEnhancedLighting()

            // 创建相机
            this.camera = new THREE.PerspectiveCamera(45, width / height, 0.01, 10000)
            this.camera.position.z = 100

            // 创建渲染器
            this.renderer = new THREE.WebGLRenderer({
                antialias: true,
                alpha: true // 允许透明度
            })
            this.renderer.setSize(width, height)
            this.renderer.setPixelRatio(window.devicePixelRatio) // 高DPI显示支持
            container.appendChild(this.renderer.domElement)

            // 设置轨道控制器
            this.controls = new OrbitControls(this.camera, this.renderer.domElement)
            this.controls.enableDamping = true
            this.controls.dampingFactor = 0.05
            this.controls.enableZoom = true
            this.controls.minDistance = 0.1
            this.controls.maxDistance = 5000

            // 加载STL模型
            this.loadSTLModel()

            window.addEventListener('resize', this.onWindowResize)
            this.startAnimation()
        },

        // 增强的光源设置
        setupEnhancedLighting() {
            // 1. 更强的环境光 - 基础照明
            const ambientLight = new THREE.AmbientLight(0xffffff, 1.2) // 强度增加到1.2
            this.scene.add(ambientLight)

            // 2. 主定向光 - 主要照明
            const mainLight = new THREE.DirectionalLight(0xffffff, 1.5) // 强度增加到1.5
            mainLight.position.set(20, 30, 20)
            mainLight.castShadow = true
            this.scene.add(mainLight)

            // 3. 辅助定向光 - 补充照明，减少阴影
            const fillLight = new THREE.DirectionalLight(0xffffff, 0.8)
            fillLight.position.set(-15, 20, -15)
            this.scene.add(fillLight)

            // 4. 背光 - 增强轮廓感
            const backLight = new THREE.DirectionalLight(0xffffff, 0.6)
            backLight.position.set(-10, 10, -20)
            this.scene.add(backLight)

            // 5. 顶光 - 从上方向下照明
            const topLight = new THREE.DirectionalLight(0xffffff, 0.7)
            topLight.position.set(0, 30, 0)
            this.scene.add(topLight)

            console.log('光源设置完成：4个定向光 + 1个环境光')
        },

        // 加载STL模型
        // 替换原来的 loadSTLModel 方法
        loadSTLModel() {
            if (!this.modelFile) {
                this.$message.error('未获取到三维模型文件')
                this.handleClose()
                return
            }

            const timestamp = new Date().getTime()
            const url = `${this.modelFile}?t=${timestamp}`
            console.log('开始加载STL文件:', url)

            this.loadProgress = 0

            const xhr = new XMLHttpRequest()
            xhr.open('GET', url, true)
            xhr.responseType = 'arraybuffer'

            // 调整比例：下载占80%，解析占20%
            const DOWNLOAD_MAX = 80
            const PARSE_MAX = 20

            xhr.onprogress = (event) => {
                if (event.lengthComputable) {
                    // 下载进度占 0-90%
                    const downloadPercent = (event.loaded / event.total) * DOWNLOAD_MAX
                    this.loadProgress = Math.min(DOWNLOAD_MAX, Math.floor(downloadPercent))

                    // 可选：显示下载速度或剩余时间
                    if (event.total && event.loaded) {
                        const remaining = event.total - event.loaded
                        const mbRemaining = (remaining / 1024 / 1024).toFixed(1)
                        // console.log(`下载进度: ${this.loadProgress}%, 剩余: ${mbRemaining}MB`)
                    }
                } else {
                    // 无 Content-Length 时的处理
                    if (this.loadProgress < DOWNLOAD_MAX - 5) {
                        this.loadProgress = Math.min(DOWNLOAD_MAX - 5, this.loadProgress + 2)
                    }
                }
            }

            xhr.onload = () => {
                if (xhr.status === 200) {
                    // 下载完成，直接跳到 90%
                    this.loadProgress = DOWNLOAD_MAX
                    console.log('文件下载完成，大小:', xhr.response.byteLength)

                    // 解析阶段占 90-100%
                    this.mostCompatibleParse(xhr.response)
                        .then(geometry => {
                            // 解析完成到 100%
                            this.loadProgress = 100
                            this.handleModelLoaded(geometry)

                            // 延迟关闭，让用户看到 100%
                            setTimeout(() => {
                                this.loading = false
                            }, 200)
                        })
                        .catch(error => {
                            console.error('解析失败:', error)
                            this.loading = false
                            this.$message.error('模型解析失败: ' + error.message)
                        })
                } else {
                    this.loading = false
                    this.$message.error(`下载失败: HTTP ${xhr.status}`)
                }
            }

            xhr.onerror = () => {
                console.error('网络错误')
                this.loading = false
                this.$message.error('网络错误，模型下载失败')
            }

            xhr.send()
        },

        // 最兼容的解析方式
        mostCompatibleParse(arrayBuffer) {
            return new Promise((resolve, reject) => {
                try {
                    const loader = new STLLoader()
                    const geometry = loader.parse(arrayBuffer)
                    console.log('STLLoader解析成功')
                    resolve(geometry)
                    return
                } catch (e1) {
                    console.log('STLLoader失败:', e1.message)
                }

                try {
                    const textDecoder = new TextDecoder('utf-8')
                    const text = textDecoder.decode(arrayBuffer)
                    if (text.includes('facet') && text.includes('vertex')) {
                        const loader = new STLLoader()
                        const geometry = loader.parse(text)
                        console.log('文本格式解析成功')
                        resolve(geometry)
                        return
                    }
                } catch (e2) {
                    console.log('文本解析失败:', e2.message)
                }

                reject(new Error('无法解析STL文件'))
            })
        },

        // 处理加载完成的模型
        handleModelLoaded(geometry) {
            this.loading = false
            this.loadProgress = 100

            try {
                if (!geometry || !geometry.attributes || !geometry.attributes.position) {
                    throw new Error('无效的几何体数据')
                }

                console.log('几何体信息:', {
                    vertices: geometry.attributes.position.count,
                    boundingBox: geometry.boundingBox,
                    boundingSphere: geometry.boundingSphere
                })

                // 关键：使用更亮、更有质感的材质
                const material = new THREE.MeshStandardMaterial({
                    color: 0xf5f5f5,          // 白玉色
                    roughness: 0.25,           // 温润光泽
                    metalness: 0.05,           // 轻微反光
                    transparent: true,
                    opacity: 0.95,
                    side: THREE.DoubleSide
                })

                this.mesh = new THREE.Mesh(geometry, material)

                // 居中模型
                geometry.computeBoundingBox()
                const center = new THREE.Vector3()
                geometry.boundingBox.getCenter(center)

                console.log('原始中心点:', center)

                geometry.translate(-center.x, -center.y, -center.z)
                geometry.computeBoundingBox()
                geometry.computeBoundingSphere()

                // 计算缩放
                const size = new THREE.Vector3()
                geometry.boundingBox.getSize(size)
                const maxDim = Math.max(size.x, size.y, size.z)

                const container = this.$refs.modelContainer
                const minContainerDim = Math.min(container.clientWidth, container.clientHeight)
                const scale = (minContainerDim * 0.6) / maxDim

                this.mesh.scale.setScalar(scale)
                console.log('应用缩放:', scale)

                // 添加到场景
                this.scene.add(this.mesh)

                // 设置相机
                this.setupArtworkCamera()

                console.log('艺术品加载完成')

            } catch (error) {
                console.error('艺术品处理错误:', error)
                this.$message.error('艺术品加载失败')
            }
        },

        // 设置相机
        setupArtworkCamera() {
            if (!this.mesh) return

            const box = new THREE.Box3().setFromObject(this.mesh)
            const size = box.getSize(new THREE.Vector3())
            const maxSize = Math.max(size.x, size.y, size.z)

            console.log('艺术品世界尺寸:', size, '最大尺寸:', maxSize)

            const fov = this.camera.fov * (Math.PI / 180)
            let baseDistance = maxSize / (2 * Math.tan(fov / 2))

            const distanceLevels = {
                default: baseDistance * 2.5,
                close: baseDistance * 0.8,
                far: baseDistance * 8
            }

            const distance = distanceLevels.default

            this.camera.position.set(0, 0, distance)
            this.camera.lookAt(0, 0, 0)
            this.controls.target.set(0, 0, 0)
            this.controls.minDistance = distanceLevels.close * 0.5
            this.controls.maxDistance = distanceLevels.far * 2
            this.controls.update()

            console.log('相机设置完成')
        },

        // 开始动画
        startAnimation() {
            this.animationId = requestAnimationFrame(() => this.animate())
        },

        // 动画循环
        animate() {
            if (!this.controls || !this.renderer || !this.scene || !this.camera) {
                return
            }

            this.animationId = requestAnimationFrame(() => this.animate())
            this.controls.update()
            this.renderer.render(this.scene, this.camera)
        },

        // 停止动画
        stopAnimation() {
            if (this.animationId) {
                cancelAnimationFrame(this.animationId)
                this.animationId = null
            }
        },

        // 窗口大小调整
        onWindowResize() {
            const container = this.$refs.modelContainer
            if (!container) return

            const width = container.clientWidth
            const height = container.clientHeight

            if (this.camera && this.renderer) {
                this.camera.aspect = width / height
                this.camera.updateProjectionMatrix()
                this.renderer.setSize(width, height)
            }
        },

        // 关闭弹窗时清理资源
        handleClose() {
            this.stopAnimation()

            if (this.renderer) {
                if (this.renderer.domElement) {
                    this.renderer.domElement.remove()
                }
                this.renderer.dispose()
                this.renderer = null
            }

            if (this.scene) {
                while (this.scene.children.length > 0) {
                    this.scene.remove(this.scene.children[0])
                }
                this.scene = null
            }

            if (this.controls) {
                this.controls.dispose()
                this.controls = null
            }

            this.camera = null
            this.mesh = null
            this.loading = false
            this.loadProgress = 0
            window.removeEventListener('resize', this.onWindowResize)
            this.dialogVisible = false
        }
    },
    watch: {
        dialogVisible(val) {
            this.$nextTick(() => {
                if (val && this.$refs.modelContainer) {
                    this.initThree()
                }
            })
        }
    },
    beforeDestroy() {
        this.handleClose()
    }
}
</script>

<style scoped>
.model-container {
    width: 100%;
    height: 500px;
    position: relative;
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    /* 渐变背景 */
}

.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.9);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 100;
}

.loading-content {
    width: 80%;
    max-width: 500px;
}

.loading-text {
    margin-bottom: 12px;
    text-align: center;
    color: #666;
    font-size: 14px;
}
</style>